import { JupiterTheme } from '@jupiterone/jupiter-web-toolkit';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import React from 'react';
import useSearchBarHintsLogic, { HookProps } from './useSearchBarHintsLogic';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    hintButton: {
      '&&': {
        marginRight: theme.spacing(-2),
        '&:hover svg': {
          color: theme.palette.coolGrey[200],
        },
        '&:after': {
          display: 'none',
        },
      },
    },
    lightbulbIcon: {
      fill: 'none',
      height: 29,
      transformOrigin: 'bottom center !important',
      stroke: 'currentColor',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      strokeMiterlimit: 10,
      strokeWidth: 2,
      width: 29,
    },
    searchButton: {
      alignSelf: 'flex-start',
      borderRadius: 0,
      height: 60,
      width: 60,
      color: theme.palette.coolGrey[300],
      position: 'relative',
      '&:after': {
        background: theme.palette.grey[300],
        content: `''`,
        display: 'block',
        width: 1,
        height: 60,
        position: 'absolute',
        right: -2,
        top: -1,
      },
      '&:hover svg': {
        color: theme.palette.primary.dark,
      },
    },
    tooltip: {
      backgroundColor: theme.palette.primary.dark,
    },
  })
);

interface Props extends HookProps {
  classes?: {
    iconButton?: string;
  };
}

const SearchBarHints = ({ searchText, classes: classesProp }: Props) => {
  const classes = useStyles();
  const { hint } = useSearchBarHintsLogic({ searchText });

  if (!hint) return null;

  const path = {
    hidden: {
      stroke: 'inherit',
    },
    visible: {
      stroke: '#007797',
    },
  };

  return (
    <Tooltip
      classes={{ tooltip: classes.tooltip }}
      title={`Pro tip: ${hint}`}
      placement="top"
    >
      <IconButton
        className={clsx(classesProp?.iconButton, classes.hintButton)}
        aria-label="Lightbulb"
      >
        <motion.svg
          className={classes.lightbulbIcon}
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          height="100%"
          viewBox="0 0 24 24"
          animate={{
            transform: [
              'rotate3d(0, 0, 1, 30deg)',
              'rotate3d(0, 0, 1, -20deg)',
              'rotate3d(0, 0, 1, 10deg)',
              'rotate3d(0, 0, 1, -10deg)',
              'rotate3d(0, 0, 1, 0deg)',
            ],
          }}
        >
          <motion.path
            initial="hidden"
            animate="visible"
            variants={path}
            d="M15.1,15.4V15a3.6,3.6,0,0,1,.2-1.1A2.178,2.178,0,0,1,16,13a5.938,5.938,0,0,0,1.8-2.9,5.664,5.664,0,0,0-.2-3.4,5.1,5.1,0,0,0-2.2-2.6A6.849,6.849,0,0,0,12,3,5.887,5.887,0,0,0,8.6,4,5.53,5.53,0,0,0,6.4,6.6,5.664,5.664,0,0,0,6.2,10,5.938,5.938,0,0,0,8,12.9a5.5,5.5,0,0,1,.7.9,2.153,2.153,0,0,1,.2,1.1v.5"
          />
          <motion.path
            initial="hidden"
            animate="visible"
            variants={path}
            d="M9.8,18h4.5"
          />
          <motion.path
            initial="hidden"
            animate="visible"
            variants={path}
            d="M10.2,21h3.5"
          />
          <motion.path
            initial="hidden"
            animate="visible"
            variants={path}
            d="M12,6.9l-1.7,3.2h3.4l-1.7,3"
          />
        </motion.svg>
      </IconButton>
    </Tooltip>
  );
};
export default SearchBarHints;
